/**
 * AdminClient Context Definitions — Service Provider role contexts
 * Redesigned: Contexts represent roles within a GRC consulting firm/cabinet
 * that manages clients and delivers services (not C-Level internal roles)
 *
 * Pattern: Immobilia PRO style (Courtier, Gestionnaire, Inspecteur...)
 * adapted to GRC service provider (Associé, Directeur Mission, Consultant...)
 */

import {
  Shield, Database, CreditCard, Crown, Eye, AlertTriangle, Scale,
  ClipboardCheck, Boxes, Target, Settings, LayoutDashboard, Users,
  Building2, FolderOpen, FolderKanban, FileText, FileCheck, Waypoints,
  BarChart3, Plug, Activity, Wrench, MessageSquare, BrainCircuit,
  Gauge, Crosshair, Siren, Bug, RefreshCw, Globe, Lock,
  Map, Layers, SearchIcon, BookOpen, Network, Calculator, ListOrdered,
  Radar, LineChart, Calendar, CalendarClock, CalendarDays, ShieldCheck, HelpCircle,
  Gavel, PanelsTopLeft, Heart, Compass, Grid2x2, TrendingUp,
  Lightbulb, Workflow, Zap, Telescope, FileCog, FlaskConical,
  GitBranch, Fingerprint, Landmark, Scale3d, Megaphone,
  GraduationCap, HeartHandshake, TreePine, Award, Puzzle,
  CircleDot, ArrowUpDown, Presentation, Blocks, Rocket,
  BadgeCheck, LibraryBig, Microscope, Cog, Palette,
  Briefcase, UserCheck, KeyRound, Clock,
  Smile, PieChart, Cpu, Star, Mic, FolderSearch, Truck,
  Building, Search, Wand2, Link2, DollarSign, Package,
  Dices, LayoutGrid, Tag, Brain, Sparkles, AlertCircle, FileSearch,
  Rss, Bell, Bot,
  type LucideIcon,
} from 'lucide-react'
import { tn } from '@/i18n/navLabels'

// ═══════════════════════════════════════════════
// CONTEXT TYPES
// ═══════════════════════════════════════════════

export type GrcContextId =
  | 'associe'
  | 'directeur-mission'
  | 'consultant-grc'
  | 'expert-securite'
  | 'expert-donnees-ia'
  | 'auditeur'
  | 'analyste'
  | 'formateur'
  | 'administration'

export type NavSectionId =
  | 'portefeuille'
  | 'prospection'
  | 'revenus'
  | 'strategie-cabinet'
  | 'dashboards-exec'
  | 'mes-mandats'
  | 'clients'
  | 'planification'
  | 'suivi-qualite'
  | 'gouvernance'
  | 'conformite'
  | 'risques'
  | 'mes-missions'
  | 'ethique'
  | 'fournisseurs'
  | 'securite'
  | 'continuite'
  | 'referentiels-secu'
  | 'donnees'
  | 'ia-responsable'
  | 'conformite-privacy'
  | 'audit-planification'
  | 'audit-execution'
  | 'audit-certification'
  | 'evaluations'
  | 'evaluation-360'
  | 'maturite'
  | 'dashboards-analyste'
  | 'changement'
  | 'formation'
  | 'ethique-formation'
  | 'workspace'
  | 'rendez-vous'
  | 'rapports'
  | 'ma-page'
  | 'mon-cabinet'
  | 'equipe'
  | 'facturation'
  | 'integrations'
  | 'aigov'
  | 'juridique'
  | 'parametres'
  | 'copilot-associe'
  | 'copilot-directeur'
  | 'copilot-consultant'
  | 'copilot-securite'
  | 'copilot-donnees'
  | 'copilot-auditeur'
  | 'copilot-analyste'
  | 'copilot-formateur'
  | 'communaute'
  | 'aide-transverse'

export interface GrcContext {
  id: GrcContextId
  name: string
  icon: LucideIcon
  description: string
  color: string
  accentBg: string
  defaultRoute: string
}

export interface NavItem {
  title: string
  icon: LucideIcon
  href?: string
  children?: NavItem[]
}

export interface NavSection {
  id: NavSectionId
  label: string
  items: NavItem[]
}

// ═══════════════════════════════════════════════
// 9 SERVICE PROVIDER CONTEXTS
// ═══════════════════════════════════════════════

export const GRC_CONTEXTS: Record<GrcContextId, GrcContext> = {
  associe: {
    id: 'associe', name: 'Associe', icon: Crown,
    description: 'Portefeuille, revenus, prospection',
    color: 'text-amber-500', accentBg: 'bg-amber-600',
    defaultRoute: '/dashboard',
  },
  'directeur-mission': {
    id: 'directeur-mission', name: 'Directeur de Mission', icon: Briefcase,
    description: 'Mandats, supervision, livrables',
    color: 'text-blue-500', accentBg: 'bg-blue-600',
    defaultRoute: '/mandats',
  },
  'consultant-grc': {
    id: 'consultant-grc', name: 'Consultant GRC', icon: Scale,
    description: 'Risques, conformite, gouvernance',
    color: 'text-teal-500', accentBg: 'bg-teal-600',
    defaultRoute: '/governance-pro',
  },
  'expert-securite': {
    id: 'expert-securite', name: 'Expert Securite', icon: Shield,
    description: 'Audits techniques, ISO 27001, incidents',
    color: 'text-red-500', accentBg: 'bg-red-600',
    defaultRoute: '/security-pro',
  },
  'expert-donnees-ia': {
    id: 'expert-donnees-ia', name: 'Expert Donnees & IA', icon: Database,
    description: 'RGPD, data governance, IA responsable',
    color: 'text-purple-500', accentBg: 'bg-purple-600',
    defaultRoute: '/data-pro',
  },
  auditeur: {
    id: 'auditeur', name: 'Auditeur', icon: ClipboardCheck,
    description: 'Missions audit, certification, constats',
    color: 'text-cyan-500', accentBg: 'bg-cyan-600',
    defaultRoute: '/audit-pro',
  },
  analyste: {
    id: 'analyste', name: 'Analyste', icon: BarChart3,
    description: 'Evaluations, maturite, benchmarks',
    color: 'text-indigo-500', accentBg: 'bg-indigo-600',
    defaultRoute: '/maturity-pro',
  },
  formateur: {
    id: 'formateur', name: 'Formateur', icon: GraduationCap,
    description: 'Sensibilisation, changement, adoption',
    color: 'text-green-500', accentBg: 'bg-green-600',
    defaultRoute: '/change-pro',
  },
  administration: {
    id: 'administration', name: 'Administration', icon: Settings,
    description: 'Organisation, equipe, facturation',
    color: 'text-slate-500', accentBg: 'bg-slate-600',
    defaultRoute: '/admin',
  },
}

export const CONTEXT_ORDER: GrcContextId[] = [
  'associe', 'directeur-mission', 'consultant-grc', 'expert-securite',
  'expert-donnees-ia', 'auditeur', 'analyste', 'formateur', 'administration',
]

// ═══════════════════════════════════════════════
// VISIBILITY MATRIX — which sections each context sees
// ═══════════════════════════════════════════════

const VISIBILITY: Record<GrcContextId, NavSectionId[]> = {
  associe: [
    'portefeuille', 'prospection', 'revenus', 'strategie-cabinet',
    'dashboards-exec', 'evaluation-360', 'copilot-associe', 'rapports', 'workspace', 'rendez-vous', 'ma-page',
  ],
  'directeur-mission': [
    'mes-mandats', 'clients', 'planification', 'suivi-qualite',
    'gouvernance', 'conformite', 'risques', 'evaluation-360', 'copilot-directeur', 'rapports', 'workspace', 'rendez-vous',
  ],
  'consultant-grc': [
    'mes-missions', 'gouvernance', 'risques', 'conformite',
    'ethique', 'fournisseurs', 'evaluations', 'evaluation-360', 'copilot-consultant', 'rapports', 'workspace', 'rendez-vous',
  ],
  'expert-securite': [
    'mes-missions', 'securite', 'continuite', 'referentiels-secu',
    'risques', 'conformite', 'audit-execution', 'copilot-securite', 'rapports', 'workspace', 'rendez-vous',
  ],
  'expert-donnees-ia': [
    'mes-missions', 'donnees', 'ia-responsable', 'conformite-privacy',
    'gouvernance', 'fournisseurs', 'evaluations', 'copilot-donnees', 'rapports', 'workspace', 'rendez-vous',
  ],
  auditeur: [
    'mes-missions', 'audit-planification', 'audit-execution',
    'audit-certification', 'conformite', 'risques', 'evaluation-360', 'copilot-auditeur', 'rapports', 'workspace', 'rendez-vous',
  ],
  analyste: [
    'mes-missions', 'evaluations', 'evaluation-360', 'maturite', 'dashboards-analyste',
    'risques', 'conformite', 'strategie-cabinet', 'copilot-analyste', 'rapports', 'workspace', 'rendez-vous',
  ],
  formateur: [
    'mes-missions', 'changement', 'formation', 'ethique-formation',
    'evaluations', 'maturite', 'copilot-formateur', 'rapports', 'workspace', 'rendez-vous',
  ],
  administration: [
    'mon-cabinet', 'equipe', 'clients', 'evaluation-360', 'facturation',
    'integrations', 'aigov', 'juridique', 'rendez-vous', 'ma-page', 'parametres',
  ],
}

// ═══════════════════════════════════════════════
// SECTION LABELS
// ═══════════════════════════════════════════════

const SECTION_LABELS: Record<NavSectionId, string> = {
  portefeuille: 'Portefeuille',
  prospection: 'Prospection & Developpement',
  revenus: 'Revenus & Facturation',
  'strategie-cabinet': 'Strategie Cabinet',
  'dashboards-exec': 'Dashboards Executifs',
  'mes-mandats': 'Mes Mandats',
  clients: 'Clients',
  planification: 'Planification',
  'suivi-qualite': 'Suivi & Qualite',
  gouvernance: 'Gouvernance',
  conformite: 'Conformite',
  risques: 'Risques',
  'mes-missions': 'Mes Missions',
  ethique: 'Ethique & ESG',
  fournisseurs: 'Fournisseurs (TPRM)',
  securite: 'Securite',
  continuite: 'Continuite & Resilience',
  'referentiels-secu': 'Referentiels Securite',
  donnees: 'Gouvernance Donnees',
  'ia-responsable': 'IA Responsable',
  'conformite-privacy': 'Vie Privee & RGPD',
  'audit-planification': 'Planification Audit',
  'audit-execution': 'Execution Audit',
  'audit-certification': 'Certification',
  evaluations: 'Evaluations',
  'evaluation-360': 'Evaluation & 360°',
  maturite: 'Maturite',
  'dashboards-analyste': 'Dashboards & Analytique',
  changement: 'Gestion du Changement',
  formation: 'Formation & Sensibilisation',
  'ethique-formation': 'Ethique & Culture',
  workspace: 'Workspace',
  'rendez-vous': 'Rendez-vous',
  rapports: 'Rapports',
  'ma-page': 'Ma Page',
  'mon-cabinet': 'Mon Cabinet',
  equipe: 'Equipe',
  facturation: 'Facturation & Abonnement',
  integrations: 'Integrations',
  aigov: 'AIGOV360 - Veille & Packs',
  juridique: 'Juridique',
  parametres: 'Parametres',
  'copilot-associe': 'AI Copilot',
  'copilot-directeur': 'AI Copilot',
  'copilot-consultant': 'AI Copilot',
  'copilot-securite': 'AI Copilot',
  'copilot-donnees': 'AI Copilot',
  'copilot-auditeur': 'AI Copilot',
  'copilot-analyste': 'AI Copilot',
  'copilot-formateur': 'AI Copilot',
  communaute: 'Communaute & Social',
  'aide-transverse': 'Outils & Assistance',
}

// ═══════════════════════════════════════════════
// COMPLETE SECTION NAV ITEMS
// ═══════════════════════════════════════════════

export const SECTION_NAV_ITEMS: Record<NavSectionId, NavItem[]> = {

  // ─────────────────────────────────────────────
  // ASSOCIÉ SECTIONS
  // ─────────────────────────────────────────────

  portefeuille: [
    { title: 'Dashboard Cabinet', icon: LayoutDashboard, href: '/dashboard' },
    { title: 'Mes Clients', icon: Building2, href: '/clients' },
    { title: 'Vue Globale', icon: Globe, children: [
      { title: 'Tous les Dossiers', icon: FolderOpen, href: '/global/dossiers' },
      { title: 'Documents', icon: FileText, href: '/global/documents' },
      { title: 'Signatures', icon: Fingerprint, href: '/global/signatures' },
      { title: 'Calendrier', icon: Calendar, href: '/global/calendar' },
      { title: 'Messages', icon: MessageSquare, href: '/global/messages' },
    ]},
  ],

  prospection: [
    { title: 'Pipeline Prospection', icon: FolderKanban, href: '/prospection' },
    { title: 'Marketing', icon: Megaphone, href: '/marketing' },
    { title: 'Offres & Devis', icon: FileText, href: '/offers' },
    { title: 'Relations', icon: Waypoints, href: '/relationships' },
    { title: 'Contrats', icon: FileCheck, href: '/contracts' },
    { title: 'Nouveau Mandat', icon: FolderOpen, href: '/mandats/new' },
  ],

  revenus: [
    { title: 'Facturation', icon: CreditCard, href: '/billing' },
    { title: 'Factures', icon: FileText, children: [
      { title: 'Mes Factures', icon: FileText, href: '/billing' },
      { title: 'Moyens de Paiement', icon: CreditCard, href: '/billing/payment-methods' },
      { title: 'Recus Fiscaux', icon: FileCheck, href: '/billing/tax-receipts' },
    ]},
    { title: 'Credits & Quotas', icon: Gauge, children: [
      { title: 'Credits IA', icon: BrainCircuit, href: '/account/credits' },
      { title: 'Acheter Credits', icon: CreditCard, href: '/account/credits/buy' },
      { title: 'Historique', icon: FileText, href: '/account/credits/history' },
      { title: 'Usage', icon: BarChart3, href: '/account/credits/usage' },
      { title: 'Quotas', icon: Gauge, href: '/account/quotas' },
    ]},
    { title: 'Plans & Abonnement', icon: Crown, children: [
      { title: 'Mon Abonnement', icon: Crown, href: '/account/subscription' },
      { title: 'Plans Disponibles', icon: Layers, href: '/account/plans' },
      { title: 'Modules Actifs', icon: Puzzle, href: '/account/features' },
    ]},
  ],

  'strategie-cabinet': [
    { title: 'Cockpit OKR', icon: Target, href: '/strategy-pro/cockpit' },
    { title: 'Cockpit PSA', icon: Gauge, href: '/strategy-pro/psa' },
    { title: 'Vision & Analyse', icon: Telescope, children: [
      { title: 'Vision', icon: Eye, href: '/strategy-pro/vision' },
      { title: 'PESTEL', icon: Globe, href: '/strategy-pro/pestel' },
      { title: 'SWOT', icon: Grid2x2, href: '/strategy-pro/swot' },
      { title: 'Carte Strategique', icon: Map, href: '/strategy-pro/strategic-map' },
    ]},
    { title: 'Execution', icon: Rocket, children: [
      { title: 'Business Case', icon: Calculator, href: '/strategy-pro/business-case' },
      { title: 'ROI', icon: TrendingUp, href: '/strategy-pro/roi' },
      { title: 'Objectifs', icon: CircleDot, href: '/strategy-pro/objectives' },
      { title: 'Key Results', icon: TrendingUp, href: '/strategy-pro/key-results' },
      { title: 'Initiatives', icon: Zap, href: '/strategy-pro/initiatives' },
      { title: 'Portfolio', icon: Layers, href: '/strategy-pro/portfolio' },
    ]},
    { title: 'Business Model', icon: Compass, children: [
      { title: 'Business Model Canvas', icon: Grid2x2, href: '/business-model-pro/business-model' },
      { title: 'Lean Canvas', icon: Blocks, href: '/business-model-pro/lean-canvas' },
      { title: 'Value Proposition', icon: Target, href: '/business-model-pro/value-proposition' },
    ]},
  ],

  'dashboards-exec': [
    { title: 'Panorama', icon: Globe, href: '/dashboard-pro/panorama' },
    { title: 'Vues Executives', icon: Crown, children: [
      { title: 'CEO / Panorama', icon: Crown, href: '/dashboard-pro/panorama' },
      { title: 'CFO / C-Suite', icon: CreditCard, href: '/dashboard-pro/csuite' },
      { title: 'Score Global', icon: Gauge, href: '/dashboard-pro/score' },
      { title: 'KPIs', icon: BarChart3, href: '/dashboard-pro/kpis' },
      { title: 'Multi-Site', icon: Globe, href: '/dashboard-pro/multi-site' },
    ]},
    { title: 'Analytique', icon: LineChart, children: [
      { title: 'Benchmarks', icon: BarChart3, href: '/dashboard-pro/benchmarks' },
      { title: 'Tendances', icon: TrendingUp, href: '/dashboard-pro/tendances' },
      { title: 'Correlations', icon: Network, href: '/dashboard-pro/correlations' },
      { title: 'Heatmap', icon: Grid2x2, href: '/dashboard-pro/heatmap' },
      { title: 'Score Consolide', icon: Gauge, href: '/dashboard-pro/score-consolide' },
      { title: 'Rapport CA', icon: Presentation, href: '/dashboard-pro/rapport-ca' },
    ]},
    { title: 'Cockpit PSA', icon: Gauge, href: '/dashboard-pro/psa' },
    { title: 'Feuilles de temps', icon: Clock, href: '/dashboard-pro/psa/timesheet' },
  ],

  // ─────────────────────────────────────────────
  // DIRECTEUR DE MISSION SECTIONS
  // ─────────────────────────────────────────────

  'mes-mandats': [
    { title: 'Dashboard Mandats', icon: LayoutDashboard, href: '/dashboard' },
    { title: 'Dossiers & Mandats', icon: FolderOpen, href: '/mandats' },
    { title: 'Nouveau Mandat', icon: FolderKanban, href: '/mandats/new' },
    { title: 'Calendrier', icon: Calendar, href: '/global/calendar' },
    { title: 'Vue Globale', icon: Globe, children: [
      { title: 'Tous les Dossiers', icon: FolderOpen, href: '/global/dossiers' },
      { title: 'Documents', icon: FileText, href: '/global/documents' },
      { title: 'Signatures', icon: Fingerprint, href: '/global/signatures' },
      { title: 'Messages', icon: MessageSquare, href: '/global/messages' },
    ]},
  ],

  clients: [
    { title: 'Repertoire Clients', icon: Building2, href: '/clients' },
    { title: 'Relations', icon: Waypoints, href: '/relationships' },
    { title: 'Contrats', icon: FileCheck, href: '/contracts' },
    { title: 'Pipeline', icon: FolderKanban, href: '/prospection' },
    { title: 'Offres', icon: FileText, href: '/offers' },
  ],

  planification: [
    { title: 'Roadmap', icon: Map, href: '/governance-pro/roadmap' },
    { title: 'Calendrier', icon: Calendar, href: '/global/calendar' },
    { title: 'Programmes', icon: Blocks, children: [
      { title: 'Programme Data', icon: Database, href: '/governance-pro/data-program' },
      { title: 'Programme IA', icon: BrainCircuit, href: '/governance-pro/ai-program' },
      { title: 'Programme Cloud', icon: Globe, href: '/governance-pro/cloud-program' },
    ]},
  ],

  'suivi-qualite': [
    { title: 'Cockpit PSA', icon: Gauge, href: '/dashboard-pro/psa' },
    { title: 'Feuilles de temps', icon: Clock, href: '/dashboard-pro/psa/timesheet' },
    { title: 'Constats Ouverts', icon: AlertTriangle, href: '/audit-pro/findings' },
    { title: 'Actions Correctives', icon: Target, href: '/audit-pro/actions' },
    { title: 'Suivi', icon: Activity, href: '/audit-pro/tracking' },
    { title: 'Preuves', icon: FileCheck, href: '/audit-pro/evidence' },
    { title: 'Certification', icon: Award, children: [
      { title: 'Dashboard Cert', icon: BarChart3, href: '/audit-pro/cert-dashboard' },
      { title: 'Readiness', icon: Gauge, href: '/audit-pro/cert-readiness' },
      { title: 'Timeline', icon: Calendar, href: '/audit-pro/cert-timeline' },
    ]},
  ],

  // ─────────────────────────────────────────────
  // SHARED: GOUVERNANCE / CONFORMITE / RISQUES
  // ─────────────────────────────────────────────

  gouvernance: [
    { title: 'Cockpit GRC', icon: BarChart3, href: '/governance-pro/cockpit' },
    { title: 'Cadre & Structure', icon: Blocks, children: [
      { title: 'Cadre de Reference', icon: LibraryBig, href: '/governance-pro/framework' },
      { title: 'Standards', icon: Award, href: '/governance-pro/standards' },
      { title: 'Modele Gouvernance', icon: Network, href: '/governance-pro/model' },
    ]},
    { title: 'Politiques & Directives', icon: FileText, children: [
      { title: 'Politiques', icon: FileText, href: '/governance-pro/policies' },
      { title: 'Procedures', icon: ListOrdered, href: '/governance-pro/procedures' },
      { title: 'Directives', icon: BookOpen, href: '/governance-pro/directives' },
      { title: 'RACI & Roles', icon: Users, href: '/governance-pro/roles' },
    ]},
    { title: 'Analyse', icon: Telescope, children: [
      { title: 'Impact IA', icon: BrainCircuit, href: '/governance-pro/ai-impact' },
      { title: 'Analyse Strategique', icon: Telescope, href: '/governance-pro/strategic-analysis' },
      { title: 'Benchmark', icon: BarChart3, href: '/governance-pro/benchmark' },
      { title: 'Alignement', icon: Target, href: '/governance-pro/alignment' },
    ]},
    { title: 'Operations', icon: Cog, children: [
      { title: 'Processus', icon: Workflow, href: '/governance-pro/processes' },
      { title: 'Générateur de Workflows', icon: Zap, href: '/workflow-builder' },
      { title: 'Gouvernance Ops', icon: Settings, href: '/governance-pro/ops-governance' },
      { title: 'Registre Risques', icon: AlertTriangle, href: '/governance-pro/risk-register' },
      { title: 'Controles & Audit', icon: ClipboardCheck, href: '/governance-pro/controls-audit' },
    ]},
  ],

  conformite: [
    { title: 'Cockpit Multi-Reg', icon: BarChart3, href: '/compliance-pro/cockpit' },
    { title: 'Reglementations', icon: Gavel, children: [
      { title: 'RGPD', icon: Eye, href: '/compliance-pro/rgpd' },
      { title: 'Loi 25', icon: Globe, href: '/compliance-pro/loi25' },
      { title: 'CCPA', icon: Shield, href: '/compliance-pro/ccpa' },
      { title: 'PCI-DSS', icon: CreditCard, href: '/compliance-pro/pci-dss' },
      { title: 'SOX', icon: Landmark, href: '/compliance-pro/sox' },
      { title: 'ISO 27701', icon: Award, href: '/compliance-pro/iso27701' },
    ]},
    { title: 'Gestion', icon: FileCheck, children: [
      { title: 'Registre', icon: FileText, href: '/compliance-pro/register' },
      { title: 'Actions Correctives', icon: ListOrdered, href: '/compliance-pro/actions' },
      { title: 'Gap Analysis', icon: Microscope, href: '/compliance-pro/gap-analysis' },
      { title: 'Posture', icon: Gauge, href: '/compliance-pro/posture' },
    ]},
    { title: 'Vie Privee', icon: Lock, children: [
      { title: 'Consentement', icon: BadgeCheck, href: '/compliance-pro/consent' },
      { title: 'Droits (DSR)', icon: Users, href: '/compliance-pro/dsr' },
      { title: 'PIA (AIPD)', icon: AlertTriangle, href: '/compliance-pro/pia' },
      { title: 'Transferts', icon: ArrowUpDown, href: '/compliance-pro/transfers' },
    ]},
    { title: 'Copilot IA', icon: Sparkles, children: [
      { title: 'Analyse d\'écart réglementaire', icon: Microscope, href: '/compliance-pro/framework-gap' },
      { title: 'Questionnaire IA', icon: ClipboardCheck, href: '/compliance-pro/questionnaire-ia' },
    ]},
  ],

  risques: [
    { title: 'Registre Risques', icon: FileText, href: '/risk-pro/register' },
    { title: 'Identification & Analyse', icon: SearchIcon, children: [
      { title: 'Wizard Identification', icon: Lightbulb, href: '/risk-pro/identify' },
      { title: 'Scenarios', icon: GitBranch, href: '/risk-pro/scenarios' },
      { title: 'Analyse Bowtie', icon: Layers, href: '/risk-pro/bowtie' },
      { title: 'Matrice 5x5', icon: Grid2x2, href: '/risk-pro/matrix' },
      { title: 'Analyse Quantitative', icon: Calculator, href: '/risk-pro/quantitative' },
      { title: 'Simulation Monte-Carlo', icon: FlaskConical, href: '/risk-pro/montecarlo' },
    ]},
    { title: 'Traitement & Suivi', icon: Target, children: [
      { title: 'Controles', icon: FileCheck, href: '/risk-pro/controls' },
      { title: 'Plans de Traitement', icon: Target, href: '/risk-pro/treatment' },
      { title: 'KRI & Monitoring', icon: Activity, href: '/risk-pro/kri' },
      { title: 'Monitoring Continu', icon: Radar, href: '/risk-pro/monitoring' },
      { title: 'Incidents', icon: Siren, href: '/risk-pro/incidents' },
    ]},
    { title: 'Rapports Risques', icon: Presentation, children: [
      { title: 'Rapport Board', icon: Presentation, href: '/risk-pro/report-board' },
      { title: 'Rapport Risques', icon: FileText, href: '/risk-pro/report-risk' },
      { title: 'Taxonomie', icon: Network, href: '/risk-pro/taxonomy' },
      { title: 'Frameworks', icon: LibraryBig, href: '/risk-pro/frameworks' },
    ]},
  ],

  // ─────────────────────────────────────────────
  // CONSULTANT GRC SPECIFIC
  // ─────────────────────────────────────────────

  'mes-missions': [
    { title: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
    { title: 'Dossiers Assignes', icon: FolderOpen, href: '/mandats' },
    { title: 'Calendrier', icon: Calendar, href: '/global/calendar' },
    { title: 'Documents', icon: FileText, href: '/global/documents' },
  ],

  ethique: [
    { title: 'Code de Conduite', icon: BookOpen, href: '/ethics-pro/code-conduite' },
    { title: 'ESG', icon: Globe, href: '/ethics-pro/esg' },
    { title: 'Signalements', icon: Megaphone, href: '/ethics-pro/signalements' },
    { title: 'Anti-Corruption', icon: Shield, children: [
      { title: 'ISO 37001', icon: Award, href: '/ethics-pro/iso37001' },
      { title: 'Sapin II', icon: Gavel, href: '/ethics-pro/sapin-ii' },
      { title: 'Conflit d\'Interets', icon: AlertTriangle, href: '/ethics-pro/conflit-interet' },
    ]},
    { title: 'Culture', icon: Heart, children: [
      { title: 'Culture Ethique', icon: Heart, href: '/ethics-pro/culture' },
      { title: 'Diversite', icon: HeartHandshake, href: '/ethics-pro/diversite' },
      { title: 'Comite Ethique', icon: Users, href: '/ethics-pro/comite-ethique' },
    ]},
  ],

  fournisseurs: [
    { title: 'Cockpit TPRM', icon: BarChart3, href: '/vendor-pro/cockpit' },
    { title: 'Gestion', icon: Building2, children: [
      { title: 'Framework TPRM', icon: Shield, href: '/vendor-pro/tprm-framework' },
      { title: 'Due Diligence', icon: SearchIcon, href: '/vendor-pro/due-diligence' },
      { title: 'Catalogue', icon: LibraryBig, href: '/vendor-pro/catalogue' },
      { title: 'Scorecards', icon: BarChart3, href: '/vendor-pro/scorecards' },
    ]},
    { title: 'Contrats & SLA', icon: FileCheck, children: [
      { title: 'Contrats', icon: FileCheck, href: '/vendor-pro/contracts' },
      { title: 'Suivi SLA', icon: Activity, href: '/vendor-pro/sla-monitoring' },
      { title: 'Renouvellements', icon: RefreshCw, href: '/vendor-pro/renewals' },
    ]},
    { title: 'Analyse', icon: Microscope, children: [
      { title: 'Performance', icon: TrendingUp, href: '/vendor-pro/performance' },
      { title: 'Concentration', icon: Radar, href: '/vendor-pro/concentration' },
      { title: 'RGPD Fournisseurs', icon: Eye, href: '/vendor-pro/rgpd' },
      { title: 'Sous-Traitants', icon: Network, href: '/vendor-pro/subprocessors' },
    ]},
  ],

  // ─────────────────────────────────────────────
  // EXPERT SECURITE SECTIONS
  // ─────────────────────────────────────────────

  securite: [
    { title: 'Hub Securite', icon: Shield, href: '/security-pro' },
    { title: 'Cockpit NIST CSF', icon: BarChart3, href: '/security-pro/cockpit' },
    { title: 'Score Securite', icon: Gauge, href: '/security-pro/score' },
    { title: 'Operations', icon: Activity, children: [
      { title: 'Posture', icon: Crosshair, href: '/security-pro/posture' },
      { title: 'Alertes', icon: Siren, href: '/security-pro/alerts' },
      { title: 'Incidents', icon: Activity, href: '/security-pro/incidents' },
      { title: 'Threat Intelligence', icon: Telescope, href: '/security-pro/threat-intel' },
      { title: 'Vulnerabilites', icon: Bug, href: '/security-pro/vulns' },
      { title: 'Controles', icon: FileCheck, href: '/security-pro/controls' },
    ]},
  ],

  continuite: [
    { title: 'Plan Continuite (BCP)', icon: Shield, href: '/security-pro/bcp' },
    { title: 'Reprise Sinistre (DR)', icon: RefreshCw, href: '/security-pro/dr' },
    { title: 'Exercices & Tests', icon: FlaskConical, href: '/security-pro/exercises' },
  ],

  'referentiels-secu': [
    { title: 'ISO 27001', icon: Award, href: '/security-pro/iso27001' },
    { title: 'SOC 2', icon: ShieldCheck, href: '/security-pro/soc2' },
    { title: 'Frameworks', icon: LibraryBig, href: '/security-pro/frameworks' },
    { title: 'Templates', icon: FileCog, href: '/security-pro/templates' },
    { title: 'Rapports', icon: Presentation, children: [
      { title: 'Rapport Executif', icon: Presentation, href: '/security-pro/report-exec' },
      { title: 'Rapport Operationnel', icon: FileText, href: '/security-pro/report-ops' },
    ]},
  ],

  // ─────────────────────────────────────────────
  // EXPERT DONNEES & IA SECTIONS
  // ─────────────────────────────────────────────

  donnees: [
    { title: 'Cockpit DAMA', icon: BarChart3, href: '/data-pro/cockpit' },
    { title: 'Score Donnees', icon: Gauge, href: '/data-pro/score' },
    { title: 'Classification', icon: Tag, href: '/data-pro/classification' },
    { title: 'AI Readiness', icon: Brain, href: '/data-pro/ai-readiness' },
    { title: 'Gouvernance Data', icon: Landmark, children: [
      { title: 'Gouvernance', icon: Landmark, href: '/data-pro/governance' },
      { title: 'Qualite Donnees', icon: BadgeCheck, href: '/data-pro/quality' },
      { title: 'Master Data (MDM)', icon: Puzzle, href: '/data-pro/mdm' },
      { title: 'Metadata & Lineage', icon: Network, href: '/data-pro/metadata' },
      { title: 'Modelisation', icon: Blocks, href: '/data-pro/modeling' },
    ]},
    { title: 'Infrastructure', icon: Layers, children: [
      { title: 'Architecture', icon: Layers, href: '/data-pro/architecture' },
      { title: 'Stockage', icon: Database, href: '/data-pro/storage' },
      { title: 'Securite Donnees', icon: Lock, href: '/data-pro/security' },
      { title: 'Cycle de Vie', icon: RefreshCw, href: '/data-pro/lifecycle' },
      { title: 'Pipelines', icon: GitBranch, href: '/data-pro/integration' },
      { title: 'DWH & BI', icon: BarChart3, href: '/data-pro/dwh-bi' },
    ]},
    { title: 'Standards', icon: Award, children: [
      { title: 'DCAM', icon: Award, href: '/data-pro/dcam' },
      { title: 'CDMC', icon: ShieldCheck, href: '/data-pro/cdmc' },
      { title: 'Frameworks', icon: LibraryBig, href: '/data-pro/frameworks' },
    ]},
  ],

  'ia-responsable': [
    { title: 'Experts Virtuels IA', icon: Sparkles, href: '/ai-experts' },
    { title: 'Console Agents IA', icon: BrainCircuit, href: '/ai-ia-pro/agent-console' },
    { title: 'Cockpit IA', icon: BarChart3, href: '/ai-ia-pro/cockpit' },
    { title: 'Score IA', icon: Gauge, href: '/ai-ia-pro/score' },
    { title: 'Bias Assessment', icon: Scale, href: '/ai-ia-pro/bias-assessment' },
    { title: 'Impact Assessment', icon: AlertCircle, href: '/ai-ia-pro/impact-assessment' },
    { title: 'Document Intelligence', icon: FileSearch, href: '/ai-ia-pro/document-intelligence' },
    { title: 'Gouvernance IA', icon: Shield, children: [
      { title: 'Registre Modeles', icon: Boxes, href: '/ai-ia-pro/registre' },
      { title: 'Detection Biais', icon: Scale, href: '/ai-ia-pro/bias' },
      { title: 'Impact IA', icon: Zap, href: '/ai-ia-pro/impact' },
      { title: 'Readiness IA', icon: Gauge, href: '/ai-ia-pro/readiness' },
      { title: 'Classification Risque', icon: AlertTriangle, href: '/ai-ia-pro/risk-class' },
      { title: 'Gouvernance Prompts', icon: FileText, href: '/ai-ia-pro/prompt-gov' },
    ]},
    { title: 'Referentiels IA', icon: BookOpen, children: [
      { title: 'EU AI Act', icon: Gavel, href: '/ai-ia-pro/eu-ai-act' },
      { title: 'ISO 42001', icon: Award, href: '/ai-ia-pro/iso42001' },
      { title: 'NIST AI RMF', icon: Shield, href: '/ai-ia-pro/nist-ai' },
      { title: 'Frameworks', icon: LibraryBig, href: '/ai-ia-pro/frameworks' },
    ]},
  ],

  'conformite-privacy': [
    { title: 'RGPD', icon: Eye, href: '/compliance-pro/rgpd' },
    { title: 'Loi 25', icon: Globe, href: '/compliance-pro/loi25' },
    { title: 'PIA (AIPD)', icon: AlertTriangle, href: '/compliance-pro/pia' },
    { title: 'Gestion Droits', icon: Users, children: [
      { title: 'Consentement', icon: BadgeCheck, href: '/compliance-pro/consent' },
      { title: 'Droits Personnes (DSR)', icon: Users, href: '/compliance-pro/dsr' },
      { title: 'Transferts', icon: ArrowUpDown, href: '/compliance-pro/transfers' },
    ]},
    { title: 'Fournisseurs Privacy', icon: Building2, children: [
      { title: 'RGPD Fournisseurs', icon: Eye, href: '/vendor-pro/rgpd' },
      { title: 'Sous-Traitants', icon: Network, href: '/vendor-pro/subprocessors' },
    ]},
  ],

  // ─────────────────────────────────────────────
  // AUDITEUR SECTIONS
  // ─────────────────────────────────────────────

  'audit-planification': [
    { title: 'Plan Annuel', icon: Map, href: '/audit-pro/annual-plan' },
    { title: 'Wizard Planification', icon: Lightbulb, href: '/audit-pro/plan-wizard' },
    { title: 'Univers d\'Audit', icon: Globe, href: '/audit-pro/universe' },
    { title: 'Methodologie', icon: BookOpen, href: '/audit-pro/methodology' },
    { title: 'Calendrier Audit', icon: Calendar, href: '/audit-pro/calendar' },
  ],

  'audit-execution': [
    { title: 'Cockpit Audit', icon: BarChart3, href: '/audit-pro/cockpit' },
    { title: 'Missions', icon: ClipboardCheck, href: '/audit-pro/missions' },
    { title: 'Constats', icon: AlertTriangle, href: '/audit-pro/findings' },
    { title: 'Actions Correctives', icon: Target, href: '/audit-pro/actions' },
    { title: 'Suivi', icon: Activity, href: '/audit-pro/tracking' },
    { title: 'Preuves', icon: FileCheck, href: '/audit-pro/evidence' },
    { title: 'Checklists', icon: ListOrdered, href: '/audit-pro/checklists' },
    { title: 'Engagement', icon: Waypoints, href: '/audit-pro/engagement' },
    { title: 'Auto-Evaluation', icon: Microscope, href: '/audit-pro/self-assess' },
    { title: 'Rapport Audit', icon: FileText, href: '/audit-pro/reports' },
    { title: 'Comite Audit', icon: Users, href: '/audit-pro/committee' },
  ],

  'audit-certification': [
    { title: 'Dashboard Certification', icon: BarChart3, href: '/audit-pro/cert-dashboard' },
    { title: 'Analyse Gaps', icon: Microscope, href: '/audit-pro/cert-gap' },
    { title: 'Readiness', icon: Gauge, href: '/audit-pro/cert-readiness' },
    { title: 'Timeline', icon: Calendar, href: '/audit-pro/cert-timeline' },
    { title: 'Projets', icon: Rocket, children: [
      { title: 'Projets Certification', icon: Rocket, href: '/audit-pro/cert-projects' },
      { title: 'Wizard Projet', icon: Lightbulb, href: '/audit-pro/cert-project-wizard' },
    ]},
    { title: 'Controles & Preuves', icon: FileCheck, children: [
      { title: 'Controles', icon: FileCheck, href: '/audit-pro/cert-controls' },
      { title: 'Decision', icon: Scale, href: '/audit-pro/cert-decision' },
      { title: 'Demandes Preuves', icon: FileText, href: '/audit-pro/cert-evidence-requests' },
      { title: 'Exigences Doc', icon: BookOpen, href: '/audit-pro/cert-doc-requirements' },
    ]},
    { title: 'Organismes', icon: Building2, children: [
      { title: 'Surveillance', icon: Eye, href: '/audit-pro/cert-surveillance' },
      { title: 'Espace Audit', icon: PanelsTopLeft, href: '/audit-pro/cert-audit-workspace' },
      { title: 'Portail Organisme', icon: Building2, href: '/audit-pro/cert-body-portal' },
      { title: 'Registre Organismes', icon: LibraryBig, href: '/audit-pro/cert-body-registry' },
    ]},
  ],

  // ─────────────────────────────────────────────
  // ANALYSTE SECTIONS
  // ─────────────────────────────────────────────

  evaluations: [
    { title: 'Evaluations Client', icon: ClipboardCheck, href: '/maturity-pro/questionnaires' },
    { title: 'Gap Analyse', icon: Microscope, href: '/maturity-pro/gap-analyse' },
    { title: 'Progression', icon: TrendingUp, href: '/maturity-pro/progression' },
    { title: 'Objectifs', icon: Target, href: '/maturity-pro/objectifs' },
    { title: 'Score', icon: Gauge, href: '/maturity-pro/score' },
  ],

  // ─────────────────────────────────────────────
  // ÉVALUATION & 360° — evaluate consultants/auditors/providers/missions
  // AND be evaluated by clients (evaluation-service).
  // ─────────────────────────────────────────────

  'evaluation-360': [
    { title: 'Vue d\'ensemble', icon: LayoutDashboard, href: '/evaluation-pro' },
    { title: 'Criteres & modeles', icon: ListOrdered, href: '/evaluation-pro/criteria' },
    { title: 'Cycles', icon: Rocket, href: '/evaluation-pro/cycles' },
    { title: 'Evaluations', icon: ClipboardCheck, href: '/evaluation-pro/evaluations' },
    { title: 'Scorecards & classements', icon: Award, href: '/evaluation-pro/scorecards' },
    { title: 'Analytique', icon: BarChart3, href: '/evaluation-pro/analytics' },
    { title: 'Plans d\'action', icon: Target, href: '/evaluation-pro/actions' },
  ],

  maturite: [
    { title: 'Cockpit Maturite', icon: BarChart3, href: '/maturity-pro/cockpit' },
    { title: 'DCMM (Data/IA/Cloud)', icon: TrendingUp, href: '/maturity-pro/dcmm' },
    { title: 'Modeles', icon: BookOpen, children: [
      { title: 'CMMI', icon: Layers, href: '/maturity-pro/cmmi' },
      { title: 'COBIT', icon: Shield, href: '/maturity-pro/cobit' },
      { title: 'ISO 33001', icon: Award, href: '/maturity-pro/iso33001' },
      { title: 'Modele Personnalise', icon: Palette, href: '/maturity-pro/custom-model' },
      { title: 'Dimensions', icon: Blocks, href: '/maturity-pro/dimensions' },
      { title: 'Mapping', icon: Network, href: '/maturity-pro/mapping' },
    ]},
    { title: 'Resultats', icon: Presentation, children: [
      { title: 'Benchmarking', icon: BarChart3, href: '/maturity-pro/benchmarking' },
      { title: 'Benchmarks', icon: BarChart3, href: '/maturity-pro/benchmarks' },
      { title: 'Campagnes', icon: Rocket, href: '/maturity-pro/campaigns' },
      { title: 'Certification', icon: Award, href: '/maturity-pro/certification' },
      { title: 'Roadmap', icon: Map, href: '/maturity-pro/roadmap' },
      { title: 'Frameworks', icon: LibraryBig, href: '/maturity-pro/frameworks' },
    ]},
  ],

  'dashboards-analyste': [
    { title: 'Cockpit PSA', icon: Gauge, href: '/dashboard-pro/psa' },
    { title: 'Feuilles de temps', icon: Clock, href: '/dashboard-pro/psa/timesheet' },
    { title: 'Score Consolide', icon: Gauge, href: '/dashboard-pro/score-consolide' },
    { title: 'Heatmap', icon: Grid2x2, href: '/dashboard-pro/heatmap' },
    { title: 'Correlations', icon: Network, href: '/dashboard-pro/correlations' },
    { title: 'Tendances', icon: TrendingUp, href: '/dashboard-pro/tendances' },
    { title: 'Drill-Down', icon: Microscope, href: '/dashboard-pro/drill-down' },
    { title: 'Comparaison', icon: Scale3d, href: '/dashboard-pro/comparaison' },
    { title: 'Benchmarks', icon: BarChart3, href: '/dashboard-pro/benchmarks' },
    { title: 'Maturite Globale', icon: TrendingUp, href: '/dashboard-pro/maturite-globale' },
    { title: 'Conformite Unifiee', icon: Scale, href: '/dashboard-pro/conformite-unifiee' },
    { title: 'Risque Agregee', icon: AlertTriangle, href: '/dashboard-pro/risque-agregee' },
  ],

  // ─────────────────────────────────────────────
  // FORMATEUR SECTIONS
  // ─────────────────────────────────────────────

  changement: [
    { title: 'Cockpit Changement', icon: BarChart3, href: '/change-pro/cockpit' },
    { title: 'ADKAR', icon: Sparkles, href: '/change-pro/adkar-assessment' },
    { title: 'Modeles', icon: BookOpen, children: [
      { title: 'ADKAR', icon: Layers, href: '/change-pro/adkar' },
      { title: 'Kotter 8 Etapes', icon: ListOrdered, href: '/change-pro/kotter' },
      { title: 'Lewin-Bridges', icon: ArrowUpDown, href: '/change-pro/lewin-bridges' },
      { title: 'McKinsey 7S', icon: Blocks, href: '/change-pro/mckinsey-7s' },
    ]},
    { title: 'Execution', icon: Rocket, children: [
      { title: 'Roadmap', icon: Map, href: '/change-pro/roadmap' },
      { title: 'Champions', icon: Crown, href: '/change-pro/champions' },
      { title: 'Adoption', icon: TrendingUp, href: '/change-pro/adoption' },
      { title: 'Resistance', icon: AlertTriangle, href: '/change-pro/resistance' },
      { title: 'Communication', icon: Megaphone, href: '/change-pro/communication' },
      { title: 'Impact', icon: Zap, href: '/change-pro/impact' },
      { title: 'Culture', icon: Heart, href: '/change-pro/culture' },
    ]},
    { title: 'Mesure', icon: Gauge, children: [
      { title: 'eNPS', icon: BarChart3, href: '/change-pro/enps' },
      { title: 'ROI', icon: TrendingUp, href: '/change-pro/roi' },
      { title: 'Score', icon: Gauge, href: '/change-pro/score' },
      { title: 'Maturite', icon: TrendingUp, href: '/change-pro/maturite' },
      { title: 'Portfolio', icon: Layers, href: '/change-pro/portfolio' },
    ]},
  ],

  formation: [
    { title: 'Formation Changement', icon: GraduationCap, href: '/change-pro/formation' },
    { title: 'Formation Ethique', icon: Heart, href: '/ethics-pro/formation' },
    { title: 'Sensibilisation', icon: Megaphone, children: [
      { title: 'Communication', icon: Megaphone, href: '/change-pro/communication' },
      { title: 'Adoption', icon: TrendingUp, href: '/change-pro/adoption' },
      { title: 'Resistance', icon: AlertTriangle, href: '/change-pro/resistance' },
    ]},
  ],

  'ethique-formation': [
    { title: 'Code de Conduite', icon: BookOpen, href: '/ethics-pro/code-conduite' },
    { title: 'Culture Ethique', icon: Heart, href: '/ethics-pro/culture' },
    { title: 'Diversite & Inclusion', icon: HeartHandshake, href: '/ethics-pro/diversite' },
    { title: 'ESG', icon: Globe, href: '/ethics-pro/esg' },
  ],

  // ─────────────────────────────────────────────
  // SHARED: WORKSPACE / RAPPORTS
  // ─────────────────────────────────────────────

  workspace: [
    { title: 'Chat IA', icon: MessageSquare, href: '/workspace-pro/chat' },
    { title: 'Documents', icon: FileText, href: '/workspace-pro/documents' },
    { title: 'Agents IA', icon: BrainCircuit, href: '/workspace-pro/agents' },
    { title: 'Recherche', icon: SearchIcon, href: '/workspace-pro/search' },
    { title: 'Communaute', icon: Users, href: '/workspace-pro/community' },
  ],

  'rendez-vous': [
    { title: 'Calendrier & Disponibilités', icon: Calendar, href: '/rendez-vous' },
    { title: 'Rendez-vous reçus', icon: CalendarClock, href: '/rendez-vous?tab=inbox' },
  ],

  rapports: [
    { title: 'Rapport Executif', icon: Presentation, href: '/governance-pro/reports' },
    { title: 'Templates', icon: FileCog, href: '/governance-pro/templates' },
    { title: 'Framework Engine', icon: LibraryBig, href: '/framework-engine' },
    { title: 'Regie Publicitaire', icon: Megaphone, href: '/ads' },
  ],

  // ─────────────────────────────────────────────
  // MA PAGE — public storefront / page builder (paid add-on)
  // ─────────────────────────────────────────────

  'ma-page': [
    { title: 'Builder', icon: Blocks, href: '/ma-page/builder' },
    { title: 'Apercu public', icon: Globe, href: '/ma-page/builder?tab=preview' },
    { title: 'Espaces RAG', icon: BrainCircuit, href: '/ma-page/builder?tab=rag' },
    { title: 'Parametres', icon: Settings, href: '/ma-page/builder?tab=settings' },
  ],

  // ─────────────────────────────────────────────
  // ADMINISTRATION SECTIONS
  // ─────────────────────────────────────────────

  'mon-cabinet': [
    { title: 'Dashboard Cabinet', icon: LayoutDashboard, href: '/dashboard' },
    { title: 'Cockpit PSA', icon: Gauge, href: '/dashboard-pro/psa' },
    { title: 'Feuilles de temps', icon: Clock, href: '/dashboard-pro/psa/timesheet' },
    { title: 'Mon Cabinet', icon: Building2, href: '/admin' },
    { title: 'Organisations', icon: Building2, href: '/admin/organizations' },
    { title: 'Hierarchie', icon: Network, href: '/admin/organizations/hierarchy' },
    { title: 'Audit Interne', icon: ShieldCheck, href: '/admin/audit' },
  ],

  equipe: [
    { title: 'Equipe', icon: Users, href: '/team' },
    { title: 'Roles & Permissions', icon: Lock, href: '/team/roles' },
    { title: 'Invitations', icon: UserCheck, href: '/team/invitations' },
  ],

  facturation: [
    { title: 'Facturation', icon: CreditCard, href: '/billing' },
    { title: 'Moyens de Paiement', icon: CreditCard, href: '/billing/payment-methods' },
    { title: 'Recus Fiscaux', icon: FileCheck, href: '/billing/tax-receipts' },
    { title: 'Abonnement & Plans', icon: Crown, children: [
      { title: 'Mon Abonnement', icon: Crown, href: '/account/subscription' },
      { title: 'Plans', icon: Layers, href: '/account/plans' },
      { title: 'Modules Actifs', icon: Puzzle, href: '/account/features' },
    ]},
    { title: 'Credits IA', icon: BrainCircuit, children: [
      { title: 'Credits', icon: BrainCircuit, href: '/account/credits' },
      { title: 'Acheter', icon: CreditCard, href: '/account/credits/buy' },
      { title: 'Historique', icon: FileText, href: '/account/credits/history' },
      { title: 'Transfert', icon: ArrowUpDown, href: '/account/credits/transfer' },
      { title: 'Usage', icon: BarChart3, href: '/account/credits/usage' },
      { title: 'Quotas', icon: Gauge, href: '/account/quotas' },
    ]},
  ],

  integrations: [
    { title: 'Cles API', icon: KeyRound, href: '/admin/api-keys' },
    { title: 'Webhooks', icon: Plug, href: '/admin/webhooks' },
    { title: 'Integrations', icon: Plug, href: '/admin/integrations' },
  ],

  aigov: [
    { title: 'Sources de Veille', icon: Telescope, href: '/admin/aigov/watch-sources' },
    { title: 'Packs de Juridiction', icon: Layers, href: '/admin/aigov/packs' },
    { title: 'Routage & Digests', icon: Waypoints, href: '/admin/aigov/routing' },
    { title: 'Statuts Legaux', icon: Scale, href: '/admin/aigov/legal-status' },
    { title: 'Changements de Pack (PCR)', icon: GitBranch, href: '/admin/aigov/pack-changes' },
    { title: 'KPIs de Veille', icon: Gauge, href: '/admin/aigov/watch-kpis' },
  ],

  juridique: [
    { title: 'Hub Juridique', icon: Gavel, href: '/legal' },
    { title: 'Veille Reglementaire', icon: Eye, href: '/legal/veille-reglementaire' },
    { title: 'Obligations', icon: FileCheck, href: '/legal/obligations-contractuelles' },
    { title: 'Protection Donnees', icon: ShieldCheck, href: '/legal/protection-donnees' },
    { title: 'Contentieux', icon: AlertTriangle, href: '/legal/contentieux' },
    { title: 'Gouvernance Corp.', icon: Building2, href: '/legal/gouvernance-corporative' },
    { title: 'Fiscalite', icon: CreditCard, href: '/legal/fiscalite' },
  ],

  parametres: [
    { title: 'Profil', icon: Users, href: '/settings/profile' },
    { title: 'Notifications', icon: Activity, href: '/settings/notifications' },
    { title: 'Securite', icon: ShieldCheck, href: '/settings/security' },
    { title: 'Developpeur', icon: Cog, href: '/settings/developer' },
    { title: 'Vie Privee', icon: Lock, href: '/settings/privacy' },
  ],

  // ─────────────────────────────────────────────
  // AI COPILOT SECTIONS (per context)
  // ─────────────────────────────────────────────

  'copilot-associe': [
    { title: 'Pipeline Intelligence', icon: TrendingUp, href: '/strategy-pro/pipeline-intelligence' },
    { title: 'Prevision Revenus', icon: DollarSign, href: '/strategy-pro/revenue-forecast' },
    { title: 'Sante Clients', icon: Heart, href: '/strategy-pro/client-health-score' },
    { title: 'Cross-Sell IA', icon: Lightbulb, href: '/strategy-pro/cross-sell-recommender' },
    { title: 'Rapport CA', icon: Presentation, href: '/strategy-pro/board-report-generator' },
    { title: 'Benchmark Cabinet', icon: BarChart3, href: '/strategy-pro/cabinet-benchmark' },
    { title: 'Deal Room', icon: Briefcase, href: '/strategy-pro/deal-room' },
    { title: 'NPS/CSAT Clients', icon: Smile, href: '/strategy-pro/client-nps-csat' },
    { title: 'Attribution Revenus', icon: PieChart, href: '/strategy-pro/revenue-attribution' },
    { title: 'Analyse Win/Loss', icon: Target, href: '/strategy-pro/competitive-win-loss' },
    { title: 'Ecosysteme Partenaires', icon: Network, href: '/strategy-pro/partner-ecosystem' },
  ],

  'copilot-directeur': [
    { title: 'Planificateur Mission', icon: Map, href: '/strategy-pro/mission-planner' },
    { title: 'Portes Qualite', icon: ShieldCheck, href: '/strategy-pro/quality-gate' },
    { title: 'Redaction Livrables', icon: FileText, href: '/strategy-pro/deliverable-drafting' },
    { title: 'Hub Communications', icon: MessageSquare, href: '/strategy-pro/client-comm-hub' },
    { title: 'Suivi Temps/Budget', icon: Clock, href: '/strategy-pro/time-budget-tracker' },
    { title: 'Lecons Apprises', icon: Lightbulb, href: '/strategy-pro/lessons-learned' },
    { title: 'Optimiseur Ressources', icon: Cpu, href: '/strategy-pro/resource-optimizer' },
    { title: 'Detecteur Scope Creep', icon: AlertTriangle, href: '/strategy-pro/scope-creep-detector' },
    { title: 'Suivi Attentes Client', icon: Eye, href: '/strategy-pro/client-expectation-tracker' },
    { title: 'Retrospective IA', icon: RefreshCw, href: '/strategy-pro/sprint-retrospective-ai' },
    { title: 'Score Qualite Livrables', icon: Star, href: '/strategy-pro/deliverable-quality-scorer' },
  ],

  'copilot-consultant': [
    { title: 'Mappeur Referentiels', icon: GitBranch, href: '/governance-pro/framework-mapper' },
    { title: 'Assistant Tests Controle', icon: ClipboardCheck, href: '/governance-pro/control-test-wizard' },
    { title: 'Questionnaire Adaptatif', icon: BrainCircuit, href: '/governance-pro/smart-questionnaire' },
    { title: 'Collecte Preuves Auto', icon: FolderSearch, href: '/governance-pro/evidence-auto-collector' },
    { title: 'Radar Reglementaire', icon: Radar, href: '/governance-pro/regulatory-radar' },
    { title: 'Analyseur Transcriptions', icon: Mic, href: '/governance-pro/interview-transcript-analyzer' },
    { title: 'Revue IA Papiers Travail', icon: SearchIcon, href: '/governance-pro/working-paper-reviewer' },
    { title: 'Verificateur References', icon: Link2, href: '/governance-pro/cross-reference-checker' },
    { title: 'Recherche Reglementaire', icon: Search, href: '/governance-pro/regulatory-database-search' },
    { title: 'Intelligence Templates', icon: Wand2, href: '/governance-pro/template-intelligence' },
  ],

  'copilot-securite': [
    { title: 'Flux Menaces', icon: Radar, href: '/security-pro/threat-intel-feed' },
    { title: 'Analyseur Pentest', icon: Bug, href: '/security-pro/pentest-parser' },
    { title: 'Simulateur PCA/PRA', icon: FlaskConical, href: '/security-pro/bcp-dr-simulator' },
    { title: 'Maturite SOC', icon: Shield, href: '/security-pro/soc-maturity' },
    { title: 'Lieur Controles-Conformite', icon: Link2, href: '/security-pro/compliance-linker' },
    { title: 'Planificateur Red Team', icon: Siren, href: '/security-pro/red-team-planner' },
    { title: 'Risque Cyber Supply Chain', icon: Truck, href: '/security-pro/supply-chain-cyber-risk' },
    { title: 'Revue Architecture Secu', icon: Building, href: '/security-pro/security-architecture-review' },
    { title: 'Forensics IA Incidents', icon: Search, href: '/security-pro/incident-forensics' },
  ],

  'copilot-donnees': [
    { title: 'Decouverte Lignage', icon: GitBranch, href: '/ai-ia-pro/data-lineage' },
    { title: 'Score Risque Modeles IA', icon: AlertTriangle, href: '/ai-ia-pro/model-risk-scorer' },
    { title: 'Detecteur Shadow AI', icon: Eye, href: '/ai-ia-pro/shadow-ai-detector' },
    { title: 'Generateur AIPD', icon: FileText, href: '/ai-ia-pro/dpia-generator' },
    { title: 'Dashboard Qualite Donnees', icon: BarChart3, href: '/ai-ia-pro/data-quality' },
    { title: 'Evolution Schemas', icon: Database, href: '/ai-ia-pro/schema-evolution-tracker' },
    { title: 'Catalogue Data Products', icon: Package, href: '/ai-ia-pro/data-product-catalog' },
    { title: 'Cout par Requete', icon: DollarSign, href: '/ai-ia-pro/cost-per-query' },
    { title: 'Monitoring DataOps', icon: Activity, href: '/ai-ia-pro/dataops-pipeline-monitor' },
  ],

  'copilot-auditeur': [
    { title: 'Univers Audit IA', icon: Globe, href: '/audit-pro/audit-universe-ai' },
    { title: 'Generateur Papiers Travail', icon: FileText, href: '/audit-pro/workpaper-generator' },
    { title: 'Classifieur Constats', icon: Boxes, href: '/audit-pro/finding-classifier' },
    { title: 'Narrateur Rapports', icon: BookOpen, href: '/audit-pro/report-narrator' },
    { title: 'Suivi IA Recommandations', icon: Target, href: '/audit-pro/follow-up-tracker-ai' },
    { title: 'Moniteur Audit Continu', icon: Activity, href: '/audit-pro/continuous-audit-monitor' },
    { title: 'Echantillonnage Statistique', icon: BarChart3, href: '/audit-pro/statistical-sampling' },
    { title: 'Detecteur Fraude', icon: AlertTriangle, href: '/audit-pro/fraud-pattern-detector' },
    { title: 'Blockchain Preuves', icon: Lock, href: '/audit-pro/evidence-blockchain' },
    { title: 'Dashboard CAE', icon: LayoutDashboard, href: '/audit-pro/cae-dashboard' },
  ],

  'copilot-analyste': [
    { title: 'Analytique Predictive', icon: TrendingUp, href: '/dashboard-pro/predictive-analytics' },
    { title: 'Moteur Correlation', icon: GitBranch, href: '/dashboard-pro/correlation-engine' },
    { title: 'Requete Langage Naturel', icon: MessageSquare, href: '/dashboard-pro/nl-query' },
    { title: 'Benchmark Auto', icon: BarChart3, href: '/dashboard-pro/auto-benchmark' },
    { title: 'Simulation Monte Carlo', icon: Dices, href: '/dashboard-pro/monte-carlo' },
    { title: 'Reseau Bayesien', icon: Network, href: '/dashboard-pro/bayesian-network' },
    { title: 'Analyse Cohortes', icon: Users, href: '/dashboard-pro/cohort-analysis' },
    { title: 'Constructeur What-If', icon: FlaskConical, href: '/dashboard-pro/what-if-builder' },
  ],

  'copilot-formateur': [
    { title: 'Generateur Contenus', icon: Wand2, href: '/change-pro/training-content-gen' },
    { title: 'Predicteur Adoption', icon: TrendingUp, href: '/change-pro/adoption-predictor' },
    { title: 'Micro-Learning', icon: GraduationCap, href: '/change-pro/micro-learning' },
    { title: 'Hub LMS', icon: BookOpen, href: '/change-pro/lms-integration' },
    { title: 'Matrice Competences', icon: LayoutGrid, href: '/change-pro/competency-matrix' },
    { title: 'Parcours Certification', icon: Award, href: '/change-pro/certification-pathway' },
    { title: 'Analytics Apprentissage', icon: BarChart3, href: '/change-pro/learning-analytics' },
  ],

  // ─────────────────────────────────────────────
  // SHARED SOCIAL / COLLAB + TRANSVERSE (appended to every context)
  // These surfaces exist as routes in App.tsx but had no nav entry.
  // ─────────────────────────────────────────────

  communaute: [
    { title: 'Fil d\'actualite', icon: Rss, href: '/feed' },
    { title: 'Evenements', icon: CalendarDays, href: '/events' },
    { title: 'Forum', icon: MessageSquare, href: '/forum' },
    { title: 'Reservations & RDV', icon: CalendarClock, href: '/booking' },
    { title: 'Messagerie', icon: MessageSquare, href: '/messages' },
    { title: 'Notifications', icon: Bell, href: '/notifications' },
  ],

  'aide-transverse': [
    { title: 'Assistant IA', icon: Bot, href: '/assistant' },
    { title: 'Automatisation (RPA)', icon: Workflow, href: '/automation/flows' },
    { title: 'Veille Reglementaire', icon: Radar, href: '/regulatory' },
    { title: 'Centre d\'aide', icon: HelpCircle, href: '/support' },
  ],
}

// ═══════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════

export function getVisibleSections(contextId: GrcContextId): NavSectionId[] {
  return VISIBILITY[contextId] || VISIBILITY.administration
}

// ── i18n: localize nav item trees + context meta at the render choke-point ──
// French strings above are the source of truth; `tn()` swaps them to English
// when the active language is EN (falls back to French for any un-mapped tail).

/** Deep-copy a nav item tree, translating every `title`. */
function localizeItems(items: NavItem[]): NavItem[] {
  return items.map((item) => ({
    ...item,
    title: tn(item.title),
    ...(item.children ? { children: localizeItems(item.children) } : {}),
  }))
}

/** Localized {name, description} for a context (persona). */
export function getContextMeta(contextId: GrcContextId): { name: string; description: string } {
  const ctx = GRC_CONTEXTS[contextId] ?? GRC_CONTEXTS.associe
  return { name: tn(ctx.name), description: tn(ctx.description) }
}

// Shared sections appended to EVERY context so the social/collab + transverse
// surfaces (feed, forum, booking, messages, notifications, assistant, RPA,
// regulatory, support) — which have routes but were absent from every VISIBILITY
// list — are always reachable from the sidebar.
const SHARED_APPENDED_SECTIONS: NavSectionId[] = ['communaute', 'aide-transverse']

export function getNavSectionsForContext(contextId: GrcContextId): NavSection[] {
  const visible = getVisibleSections(contextId)
  const withShared = [
    ...visible,
    ...SHARED_APPENDED_SECTIONS.filter((s) => !visible.includes(s)),
  ]
  return withShared
    .filter((sectionId) => SECTION_NAV_ITEMS[sectionId])
    .map((sectionId) => ({
      id: sectionId,
      label: tn(SECTION_LABELS[sectionId] || sectionId),
      items: localizeItems(SECTION_NAV_ITEMS[sectionId]),
    }))
}
